/**
 * BundleCard - Carte d'affichage d'un bundle/pack
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Tag } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BundleCard({ bundle }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const lang = i18n.language;

  const name = bundle.name?.[lang] || bundle.name?.fr || bundle.name?.en || 'Pack';
  const description = bundle.description?.[lang] || bundle.description?.fr || '';
  
  // Calculer la réduction
  const discount = bundle.original_price > 0 
    ? Math.round((1 - bundle.bundle_price / bundle.original_price) * 100) 
    : 0;

  // Image du bundle ou première image des produits
  const imageUrl = bundle.image_url 
    ? (bundle.image_url.startsWith('/api/') ? `${API_URL}${bundle.image_url}` : bundle.image_url)
    : null;

  // Images des produits du bundle
  const productImages = bundle.products?.slice(0, 3).map(p => {
    if (p.images?.main?.[0]) {
      const img = p.images.main[0];
      return img.startsWith('/api/') ? `${API_URL}${img}` : img;
    }
    return null;
  }).filter(Boolean) || [];

  return (
    <Link 
      to={`/bundle/${bundle.id}`}
      className="group block"
      data-testid={`bundle-card-${bundle.id}`}
    >
      <div className={cn(
        "relative overflow-hidden rounded-xl border-2 transition-all duration-300",
        "bg-gradient-to-br from-purple-900/30 to-zinc-900",
        "border-purple-500/30 hover:border-purple-500",
        "hover:shadow-xl hover:shadow-purple-500/20"
      )}>
        {/* Badge réduction */}
        {discount > 0 && (
          <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-red-600 to-red-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-lg">
            -{discount}%
          </div>
        )}

        {/* Badge Pack */}
        <div className="absolute top-3 left-3 z-10 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
          <Package className="w-3 h-3" />
          PACK
        </div>

        {/* Images des produits */}
        <div className="relative h-48 bg-zinc-800/50 overflow-hidden">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : productImages.length > 0 ? (
            <div className="flex h-full">
              {productImages.map((img, idx) => (
                <div 
                  key={idx} 
                  className="flex-1 border-r border-zinc-700 last:border-0"
                >
                  <img 
                    src={img} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-16 h-16 text-purple-500/50" />
            </div>
          )}
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="text-lg font-bold text-white group-hover:text-purple-400 transition-colors">
            {name}
          </h3>
          
          {description && (
            <p className="text-sm text-gray-400 mt-1 line-clamp-2">
              {description}
            </p>
          )}

          {/* Produits inclus */}
          <div className="flex items-center gap-1 mt-3 text-xs text-gray-500">
            <Tag className="w-3 h-3" />
            <span>{bundle.product_ids?.length || 0} {t('bundle.productsIncluded', 'produits inclus')}</span>
          </div>

          {/* Prix */}
          <div className="flex items-end gap-3 mt-4">
            <span className="text-2xl font-bold text-white">
              {formatPrice(bundle.bundle_price)}
            </span>
            {bundle.original_price > bundle.bundle_price && (
              <span className="text-lg text-gray-500 line-through">
                {formatPrice(bundle.original_price)}
              </span>
            )}
          </div>

          {/* Économie */}
          {discount > 0 && (
            <p className="text-sm text-green-400 mt-2">
              {t('bundle.save', 'Économisez')} {formatPrice(bundle.original_price - bundle.bundle_price)}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default BundleCard;
